import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import mergeLodash from 'lodash/merge';
import reduce from 'lodash/reduce';

import {
  get as getState,
  getAll as getAllState,
  getByProperties as getByPropertiesState,
  merge,
} from './state';
import logger from './logger';

class ReduxRestEntity {
  static initialState = {
    data: {},
    // TODO: Avoid using error and isLoading for now (will not necessarily behave as expected)
    error: null,
    isLoading: false,
  };

  constructor(options) {
    this.name = options.name;
    this.primaryKey = options.primaryKey || 'id';
    this.api = options.api;
    const actionPrefix = options.actionPrefix ? `-${options.actionPrefix}` : '';

    this.TYPES = {
      SET_ENTITY: `${this.name}${actionPrefix}/SET_ENTITY`,
      SET_ENTITIES: `${this.name}${actionPrefix}/SET_ENTITIES`,
      CREATE_REQUEST_ERROR: `${this.name}${actionPrefix}/CREATE_REQUEST_ERROR`,
      CREATE_REQUEST_SUCCESS: `${this.name}${actionPrefix}/CREATE_REQUEST_SUCCESS`,
      CREATE_REQUEST: `${this.name}${actionPrefix}/CREATE_REQUEST`,
      DESTROY_REQUEST_ERROR: `${this.name}${actionPrefix}/DESTROY_REQUEST_ERROR`,
      DESTROY_REQUEST_SUCCESS: `${this.name}${actionPrefix}/DESTROY_REQUEST_SUCCESS`,
      DESTROY_REQUEST: `${this.name}${actionPrefix}/DESTROY_REQUEST`,
      GET_REQUEST_ERROR: `${this.name}${actionPrefix}/GET_REQUEST_ERROR`,
      GET_REQUEST_SUCCESS: `${this.name}${actionPrefix}/GET_REQUEST_SUCCESS`,
      GET_REQUEST: `${this.name}${actionPrefix}/GET_REQUEST`,
      GET_ALL_REQUEST_ERROR: `${this.name}${actionPrefix}/GET_ALL_REQUEST_ERROR`,
      GET_ALL_REQUEST_SUCCESS: `${this.name}${actionPrefix}/GET_ALL_REQUEST_SUCCESS`,
      GET_ALL_REQUEST: `${this.name}${actionPrefix}/GET_ALL_REQUEST`,
      UPDATE_REQUEST_ERROR: `${this.name}${actionPrefix}/UPDATE_REQUEST_ERROR`,
      UPDATE_REQUEST_SUCCESS: `${this.name}${actionPrefix}/UPDATE_REQUEST_SUCCESS`,
      UPDATE_REQUEST: `${this.name}${actionPrefix}/UPDATE_REQUEST`,
      SEARCH_REQUEST: `${this.name}${actionPrefix}/SEARCH_REQUEST`,
      SEARCH_REQUEST_SUCCESS: `${this.name}${actionPrefix}/SEARCH_REQUEST_SUCCESS`,
      SEARCH_REQUEST_ERROR: `${this.name}${actionPrefix}/SEARCH_REQUEST_ERROR`,
    };
  }

  getId = (entity = {}) => entity[this.primaryKey]

  set = (entity, options) => (dispatch) => {
    const updatedEntity = mergeLodash(entity, options);
    const id = this.getId(updatedEntity);
    dispatch({ type: this.TYPES.SET_ENTITY, payload: { [id]: updatedEntity } });
    return updatedEntity;
  }

  setAll = entities => (dispatch) => {
    const data = reduce(entities, (result, entity) => ({ ...result, [this.getId(entity)]: entity }), {});
    dispatch({ type: this.TYPES.SET_ENTITIES, payload: data });
    return data;
  }

  create = (data, options = {}) => async (dispatch) => {
    dispatch({ type: this.TYPES.CREATE_REQUEST });

    try {
      const response = await this.api.createEntity(data, options.parentIds, options.query);
      const entity = get(response, 'data');
      const id = this.getId(entity) || get(entity, 'id');

      dispatch({ type: this.TYPES.CREATE_REQUEST_SUCCESS, payload: { [id]: entity } });

      return response;
    } catch (error) {
      dispatch({ type: this.TYPES.CREATE_REQUEST_ERROR, payload: error });

      throw error;
    }
  }

  destroy = (id, options = {}) => async (dispatch) => {
    dispatch({ type: this.TYPES.DESTROY_REQUEST });

    try {
      const { parentIds } = options;
      const response = await this.api.destroyEntity(id, parentIds);

      dispatch({ type: this.TYPES.DESTROY_REQUEST_SUCCESS, payload: { [id]: null }, parentIds });

      return response;
    } catch (error) {
      dispatch({ type: this.TYPES.DESTROY_REQUEST_ERROR, payload: error });

      throw error;
    }
  }

  get = (id, options = {}, projection) => async (dispatch) => {
    dispatch({ type: this.TYPES.GET_REQUEST });

    const query = get(options, 'query', {});
    const name = get(options, 'name', '');
    try {
      const response = await this.api.getEntity(id, options.parentIds, query, options.fetch);
      const entity = get(response, 'data');
      const entityId = this.getId(entity);
      dispatch({ type: this.TYPES.GET_REQUEST_SUCCESS, payload: { [entityId]: entity } });

      return projection ? projection(response) : response;
    } catch (error) {
      dispatch({ type: this.TYPES.GET_REQUEST_ERROR, payload: error });
      logger.logDebug(`Get entity failed with the following ID: ${id} and Name: ${name}`, error);
      return null;
    }
  }

  getEntity = (id, options) => this.get(id, options, response => get(response, 'data'));

  getAll = options => async (dispatch) => {
    dispatch({ type: this.TYPES.GET_ALL_REQUEST });

    const query = get(options, 'query', {});
    const fetchOptions = get(options, 'fetch', {});
    const responseEntityKey = get(options, 'responseEntityKey', 'data') || 'data';

    try {
      const response = await this.api.getEntities(query, options.parentIds, fetchOptions);
      const data = reduce(get(response, responseEntityKey), (result, entity) => ({ ...result, [this.getId(entity)]: entity }), {});

      dispatch({ type: this.TYPES.GET_ALL_REQUEST_SUCCESS, payload: data });

      return response;
    } catch (error) {
      dispatch({ type: this.TYPES.GET_ALL_REQUEST_ERROR, payload: error });

      throw error;
    }
  }

  update = (id, data, options = {}) => async (dispatch) => {
    dispatch({ type: this.TYPES.UPDATE_REQUEST });

    try {
      const response = await this.api.updateEntity(id, data, options.parentIds);
      const entity = get(response, 'data');

      dispatch({ type: this.TYPES.UPDATE_REQUEST_SUCCESS, payload: { [id]: entity } });

      return response;
    } catch (error) {
      dispatch({ type: this.TYPES.UPDATE_REQUEST_ERROR, payload: error });

      throw error;
    }
  }

  search = (options, projection) => async (dispatch) => {
    dispatch({ type: this.TYPES.SEARCH_REQUEST });

    const query = get(options, 'query', {});
    const fetchOptions = get(options, 'fetch', {});

    try {
      const response = await this.api.searchEntities(query, fetchOptions);
      const data = reduce(response.data, (result, entity) => ({ ...result, [this.getId(entity)]: entity }), {});

      dispatch({ type: this.TYPES.SEARCH_REQUEST_SUCCESS, payload: data });
      return projection ? projection(response) : response;
    } catch (error) {
      dispatch({ type: this.TYPES.SEARCH_REQUEST_ERROR, payload: error });

      throw error;
    }
  };

  searchEntities = options => this.search(options, response => get(response, 'data'));

  getActions = () => ({
    set: this.set,
    setAll: this.setAll,
    create: this.create,
    destroy: this.destroy,
    get: this.get,
    getAll: this.getAll,
    update: this.update,
    search: this.search,
  })

  getTypes = () => ({
    ...this.TYPES,
  });

  getReducer = () => (state = ReduxRestEntity.initialState, action = {}) => {
    switch (action.type) {
      case this.TYPES.CREATE_REQUEST:
      case this.TYPES.DESTROY_REQUEST:
      case this.TYPES.GET_REQUEST:
      case this.TYPES.GET_ALL_REQUEST:
      case this.TYPES.SEARCH_REQUEST:
      case this.TYPES.UPDATE_REQUEST:
        return merge(state, {
          error: null,
          isLoading: true,
        });
      case this.TYPES.SET_ENTITY:
      case this.TYPES.SET_ENTITIES:
      case this.TYPES.CREATE_REQUEST_SUCCESS:
      case this.TYPES.DESTROY_REQUEST_SUCCESS:
      case this.TYPES.GET_REQUEST_SUCCESS:
      case this.TYPES.GET_ALL_REQUEST_SUCCESS:
      case this.TYPES.SEARCH_REQUEST_SUCCESS:
      case this.TYPES.UPDATE_REQUEST_SUCCESS:
        return merge(state, {
          data: merge(state.data, action.payload),
          isLoading: false,
        });
      case this.TYPES.CREATE_REQUEST_ERROR:
      case this.TYPES.DESTROY_REQUEST_ERROR:
      case this.TYPES.GET_REQUEST_ERROR:
      case this.TYPES.GET_ALL_REQUEST_ERROR:
      case this.TYPES.SEARCH_REQUEST_ERROR:
      case this.TYPES.UPDATE_REQUEST_ERROR:
        return merge(state, {
          error: action.payload,
          isLoading: false,
        });
      default:
        return state;
    }
  }

  // TODO: Consider reselect package, or shallow compare (within useSelector)
  getAllFromState = (state) => {
    const error = getState(state, `entities.${this.name}.error`);
    const isLoading = getState(state, `entities.${this.name}.isLoading`, false);
    const data = getState(state, `entities.${this.name}.data`, {});

    return {
      error,
      isLoading,
      data,
    };
  };

  getByIdFromState = (state, id) => {
    const error = getState(state, `entities.${this.name}.error`);
    const isLoading = getState(state, `entities.${this.name}.isLoading`, false);
    const data = getState(state, `entities.${this.name}.data.${id}`);
    return {
      error,
      isLoading,
      data,
    };
  };

  getDataByIdFromState = (state, id) => getState(state, `entities.${this.name}.data.${id}`);

  getByIdsFromState = (state, ids) => {
    const error = getState(state, `entities.${this.name}.error`);
    const isLoading = getState(state, `entities.${this.name}.isLoading`, false);
    const data = getAllState(getState(state, `entities.${this.name}.data`), ids) || {};

    return {
      error,
      isLoading,
      data,
    };
  };

  getByPropertiesFromState = (state, properties) => getByPropertiesState(state, `entities.${this.name}.data`, properties);

  getPropertyByNameFromState = (state, id, property, defaultValue) => getState(state, `entities.${this.name}.data.${id}.${property}`, defaultValue);

  getState = () => ({
    getAllFromState: this.getAllFromState,
    getByIdFromState: this.getByIdFromState,
    getDataByIdFromState: this.getDataByIdFromState,
    getByIdsFromState: this.getByIdsFromState,
    getByPropertiesFromState: this.getByPropertiesFromState,
    getPropertyByNameFromState: this.getPropertyByNameFromState,
  })

  getAllSelector = state => getState(state, `entities.${this.name}.data`, []);

  /**
   * Selector for retrieving a single entity by id from redux.
   * If no id is present the selector will return null.
   *
   * @param id            id of the entity to retrieve.
   * @param defaultValue  default value to return if no id present.
   */
  getByIdSelector = (id, defaultValue) => (state) => {
    if (!id && defaultValue) {
      return defaultValue;
    }

    return getState(state, `entities.${this.name}.data.${id}`, {});
  };

  /**
   * Selector for retrieving a list of entities by ids from redux.
   * If no ids are present or the ids are an empty array the selector will return null.
   *
   * @param ids           ids of the entities to retrieve.
   * @param defaultValue  default value to return if no ids present.
   */
  getByIdsSelector = (ids, defaultValue) => (state) => {
    if (isEmpty(ids) && defaultValue) {
      return defaultValue;
    }

    return getAllState(getState(state, `entities.${this.name}.data`, []), ids);
  }

  getByPropertiesSelector = properties => state => getByPropertiesState(state, `entities.${this.name}.data`, properties, {});

  getPropertyByNameSelector = (id, property, defaultValue) => state => getState(state, `entities.${this.name}.data.${id}.${property}`, defaultValue);

  getSelectors = () => ({
    getAllFromState: this.getAllSelector,
    getByIdFromState: this.getByIdSelector,
    getByIdsFromState: this.getByIdsSelector,
    getByPropertiesFromState: this.getByPropertiesSelector,
    getPropertyByNameFromState: this.getPropertyByNameSelector,
  })
}

export default ReduxRestEntity;
